import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getServices, getAvailableTimeSlots, getMonthAvailability, createAppointment, initWompiPayment, mockPayment } from '@/services/bookingService';
import type { AppointmentRecord, DayAvailabilityStatus } from '@/services/bookingService';
import { isMockMode } from '@/lib/paymentMode';
import type { BarberService, TimeSlot } from '@/types';
import StepIndicator from './components/StepIndicator';
import ServiceCard from './components/ServiceCard';
import BookingCalendar from './components/BookingCalendar';
import TimeSlotGrid from './components/TimeSlotGrid';
import { baseUrl, getUrl } from '@/lib/url';
import './page.css';

const BOOKING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Reservar Cita | Barber Studio Luisfer – Barranquilla, Colombia",
  "description": "Reserva tu cita en Barber Studio Luisfer, barbería premium en Barranquilla, Colombia. Elige tu servicio, fecha y hora en pocos pasos.",
  "url": getUrl('/booking'),
  "inLanguage": "es-CO",
  "isPartOf": {
    "@type": "WebSite",
    "name": "Barber Studio Luisfer",
    "url": baseUrl,
  },
  "potentialAction": {
    "@type": "ReserveAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": getUrl('/booking'),
      "inLanguage": "es-CO",
      "actionPlatform": [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform"
      ]
    },
    "result": {
      "@type": "Reservation",
      "name": "Reserva de cita en Barber Studio Luisfer"
    }
  }
};

function buildGoogleCalendarUrl(params: {
  serviceName: string;
  date: string;
  time: string;
  barberName?: string;
  durationMin?: number;
}): string {
  const { serviceName, date, time, barberName = 'Luisfer', durationMin = 40 } = params;
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const startUTC = new Date(Date.UTC(year, month - 1, day, hour + 5, minute, 0));
  const endUTC = new Date(startUTC.getTime() + durationMin * 60 * 1000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const text = encodeURIComponent(`${serviceName} – Barber Studio Luisfer`);
  const dates = `${fmt(startUTC)}/${fmt(endUTC)}`;
  const details = encodeURIComponent(
    `Servicio: ${serviceName}\nBarbero: ${barberName}\nDuración: ${durationMin} minutos\n\n⚠️ Por favor llega 10 minutos antes de tu cita.`
  );
  const location = encodeURIComponent('Calle 27b # 11-47, Barranquilla, Colombia');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${dates}&details=${details}&location=${location}&sf=true&output=xml`;
}

const STEPS = [
  { number: 1, label: 'Servicio' },
  { number: 2, label: 'Fecha & Hora' },
  { number: 3, label: 'Confirmación' },
  { number: 4, label: 'Pago' },
];

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [services, setServices] = useState<BarberService[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingAppointment, setPendingAppointment] = useState<AppointmentRecord | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [wompiRedirecting, setWompiRedirecting] = useState(false);
  const [dayStatuses, setDayStatuses] = useState<DayAvailabilityStatus[]>([]);
  const [calendarMonth, setCalendarMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-booking';
    script.textContent = JSON.stringify(BOOKING_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('schema-booking');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    getServices()
      .then((data) => setServices(data))
      .catch((err) => console.error('[BookingPage] getServices error:', err))
      .finally(() => setLoadingServices(false));
  }, []);

  useEffect(() => {
    if (!selectedDate) { setTimeSlots([]); return; }
    setLoadingSlots(true);
    setSelectedTime('');
    getAvailableTimeSlots(selectedDate)
      .then((slots) => setTimeSlots(slots))
      .catch((err) => console.error('[BookingPage] getAvailableTimeSlots error:', err))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  useEffect(() => {
    getMonthAvailability(calendarMonth.year, calendarMonth.month)
      .then((statuses) => setDayStatuses(statuses))
      .catch((err) => console.error('[BookingPage] getMonthAvailability error:', err));
  }, [calendarMonth.year, calendarMonth.month]);

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const selectedServiceData = services.find((s) => s.id === selectedService);

  const handleConfirm = async () => {
    if (!clientName.trim() || !clientEmail.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const appointment = await createAppointment({
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        client_phone: clientPhone.trim() || undefined,
        service_id: selectedService,
        appointment_date: selectedDate,
        start_time: selectedTime,
      });
      setPendingAppointment(appointment);
      setStep(4);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al crear la reserva';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Unified payment handler.
   * Branches on VITE_PAYMENT_MODE:
   *   'mock' → simulate locally, then navigate to result page
   *   'real' → redirect to Wompi checkout
   */
  const handlePayment = async () => {
    if (!pendingAppointment || !selectedServiceData) return;
    setProcessingPayment(true);
    setPaymentError(null);

    try {
      if (isMockMode()) {
        // ── Mock branch — amount is always $0 ────────────────────────────────
        const { reference } = await mockPayment({
          appointment: pendingAppointment,
          amountCOP: 0,
          customerEmail: clientEmail,
          customerName: clientName,
        });
        // Navigate to result page with mock success params (same shape as Wompi)
        navigate(`/reservar/resultado-pago?reference=${reference}&status=APPROVED`);
      } else {
        // ── Real branch — charge booking_fee (anticipo) ──────────────────────
        const redirectUrl = `${window.location.origin}/reservar/resultado-pago`;
        const { wompiUrl } = await initWompiPayment({
          appointment: pendingAppointment,
          amountCOP: selectedServiceData.booking_fee,
          customerEmail: clientEmail,
          customerName: clientName,
          redirectUrl,
        });
        setWompiRedirecting(true);
        // Small delay so user sees the "redirecting" state
        await new Promise((resolve) => setTimeout(resolve, 600));
        window.location.href = wompiUrl;
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al iniciar el pago';
      setPaymentError(msg);
      setProcessingPayment(false);
    }
  };

  return (
    <div className="booking-page">

      {/* Top bar */}
      <header className="booking-topbar">
        <Link to="/" className="booking-topbar-back">
          <div className="booking-topbar-back-icon">
            <i className="ri-arrow-left-line" />
          </div>
          <span className="booking-topbar-back-label">Volver al inicio</span>
        </Link>
        <div className="booking-topbar-brand">
          <span className="booking-topbar-brand-text">
            Barber Studio <span className="booking-topbar-brand-accent">Luisfer</span>
          </span>
        </div>
        <div className="w-28 hidden md:block" />
      </header>

      <main className="pt-28 pb-24 px-4 md:px-8">
        <div className="text-center mb-10 max-w-xl mx-auto">
          <p className="booking-page-label">◆ Reserva tu cita ◆</p>
          <h1 className="booking-page-title">Reservar Cita</h1>
          <p className="booking-page-subtitle">
            Elige tu servicio, selecciona fecha y hora, y confirma tu cita en pocos pasos.
          </p>
        </div>

        {!submitted ? (
          <div className="max-w-5xl mx-auto">
            <StepIndicator currentStep={step} steps={STEPS} />

            {/* ── Step 1 ── */}
            {step === 1 && (
              <div className="max-w-3xl mx-auto">
                <div className="mb-8">
                  <h2 className="booking-step-title">Elige tu servicio</h2>
                  <p className="booking-step-subtitle">Todos los servicios incluyen un tiempo estimado de 40 minutos.</p>
                </div>
                {loadingServices ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="booking-service-skeleton" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {services.map((service) => (
                      <ServiceCard key={service.id} service={service} selected={selectedService === service.id} onSelect={setSelectedService} />
                    ))}
                  </div>
                )}
                <div className="pt-8 flex justify-end">
                  <button
                    type="button"
                    disabled={!selectedService}
                    onClick={() => setStep(2)}
                    className="booking-btn-primary"
                  >
                    Siguiente <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 2 ── */}
            {step === 2 && (
              <div>
                <div className="mb-8 max-w-3xl mx-auto">
                  <h2 className="booking-step-title">Fecha &amp; Hora</h2>
                  <p className="booking-step-subtitle">Selecciona el día y la franja horaria que prefieras.</p>
                </div>
                <div className="booking-datetime-panel">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-[420px] flex-shrink-0 booking-datetime-panel-section booking-datetime-panel-divider-h lg:booking-datetime-panel-divider-h-0 lg:booking-datetime-panel-divider-v">
                      <div className="booking-datetime-panel-icon-label">
                        <div className="booking-datetime-panel-icon">
                          <i className="ri-calendar-line" />
                        </div>
                        <span className="booking-datetime-panel-label">Selecciona una fecha</span>
                      </div>
                      <BookingCalendar
                        selectedDate={selectedDate}
                        onSelectDate={(date) => { setSelectedDate(date); setSelectedTime(''); }}
                        minDate={today}
                        dayStatuses={dayStatuses}
                        onMonthChange={(year, month) => setCalendarMonth({ year, month })}
                      />
                    </div>
                    <div className="flex-1 booking-datetime-panel-section">
                      <div className="booking-datetime-panel-icon-label">
                        <div className="booking-datetime-panel-icon">
                          <i className="ri-time-line" />
                        </div>
                        <span className="booking-datetime-panel-label">Horarios disponibles</span>
                      </div>
                      <TimeSlotGrid slots={timeSlots} selectedTime={selectedTime} onSelectTime={setSelectedTime} loading={loadingSlots} selectedDate={selectedDate} />
                    </div>
                  </div>
                  <div className="booking-datetime-footer">
                    <div className="booking-datetime-footer-info">
                      {selectedDate ? (
                        <span className="booking-datetime-footer-date">
                          <i className="ri-calendar-check-line" />
                          {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                      ) : (
                        <span className="booking-datetime-footer-empty">Sin fecha seleccionada</span>
                      )}
                      {selectedTime && (
                        <>
                          <span className="booking-datetime-footer-dot">·</span>
                          <span className="booking-datetime-footer-time">
                            <i className="ri-time-line" />{selectedTime}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setStep(1)} className="booking-btn-secondary">
                        <i className="ri-arrow-left-line" /> Atrás
                      </button>
                      <button
                        type="button"
                        disabled={!selectedDate || !selectedTime || loadingSlots}
                        onClick={() => setStep(3)}
                        className="booking-btn-primary"
                      >
                        Siguiente <i className="ri-arrow-right-line" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 3 ── */}
            {step === 3 && (
              <div className="max-w-3xl mx-auto">
                <div className="mb-8">
                  <h2 className="booking-step-title">Confirma tu reserva</h2>
                  <p className="booking-step-subtitle">Introduce tus datos y revisa los detalles antes de confirmar.</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="booking-form-label">Nombre completo *</label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Ej. Carlos García"
                        className="booking-form-input"
                      />
                    </div>
                    <div>
                      <label className="booking-form-label">Correo electrónico *</label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="Ej. carlos@email.com"
                        className="booking-form-input"
                      />
                    </div>
                    <div>
                      <label className="booking-form-label">Teléfono / WhatsApp</label>
                      <input
                        type="tel"
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        placeholder="Ej. 3001234567"
                        className="booking-form-input"
                      />
                    </div>
                  </div>
                  <div className="booking-summary-card">
                    <div className="booking-summary-top-line" />
                    <div className="booking-summary-body">
                      <p className="booking-summary-section-label">Resumen</p>
                      <div className="booking-summary-row">
                        <div className="booking-summary-row-icon">
                          <i className="ri-scissors-line" />
                        </div>
                        <div>
                          <p className="booking-summary-row-sublabel">Servicio</p>
                          <p className="booking-summary-row-value">{selectedServiceData?.name}</p>
                          <p className="booking-summary-row-meta">{selectedServiceData?.currency}{selectedServiceData?.price} · {selectedServiceData?.duration} min</p>
                        </div>
                      </div>
                      <div className="booking-summary-divider" />
                      <div className="booking-summary-row">
                        <div className="booking-summary-row-icon">
                          <i className="ri-calendar-check-line" />
                        </div>
                        <div>
                          <p className="booking-summary-row-sublabel">Fecha</p>
                          <p className="booking-summary-row-value">
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="booking-summary-divider" />
                      <div className="booking-summary-row">
                        <div className="booking-summary-row-icon">
                          <i className="ri-time-line" />
                        </div>
                        <div>
                          <p className="booking-summary-row-sublabel">Hora</p>
                          <p className="booking-summary-row-value">{selectedTime}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {submitError && (
                  <div className="booking-error-banner">
                    <i className="ri-error-warning-line" />
                    <p className="booking-error-banner-text">{submitError}</p>
                  </div>
                )}
                <div className="flex justify-between pt-8">
                  <button type="button" onClick={() => setStep(2)} disabled={submitting} className="booking-btn-secondary">
                    <i className="ri-arrow-left-line" /> Atrás
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={submitting || !clientName.trim() || !clientEmail.trim()}
                    className="booking-btn-primary"
                  >
                    {submitting
                      ? (<><i className="ri-loader-4-line animate-spin" /> Procesando...</>)
                      : (<><i className="ri-arrow-right-line" /> Ir al pago</>)
                    }
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 4 ── */}
            {step === 4 && pendingAppointment && (
              <div className="max-w-2xl mx-auto">
                <div className="mb-8">
                  <h2 className="booking-step-title">Pago</h2>
                  <p className="booking-step-subtitle">Tu cita está reservada y pendiente de pago. Completa el pago para confirmarla.</p>
                </div>
                <div className="booking-payment-pending-badge">
                  <div className="booking-payment-pending-icon">
                    <i className="ri-time-line" />
                  </div>
                  <div>
                    <p className="booking-payment-pending-title">Reserva pendiente de pago</p>
                    <p className="booking-payment-pending-id">ID: <span>{pendingAppointment.id.slice(0, 8).toUpperCase()}</span></p>
                  </div>
                </div>
                <div className="booking-payment-order-card">
                  <div className="booking-payment-order-top-line" />
                  <div className="booking-payment-order-body">
                    <p className="booking-payment-order-label">Resumen del pedido</p>
                    <div className="booking-payment-order-row">
                      <div className="booking-payment-order-service">
                        <div className="booking-payment-order-service-icon">
                          <i className="ri-scissors-line" />
                        </div>
                        <div>
                          <p className="booking-payment-order-service-name">{selectedServiceData?.name}</p>
                          <p className="booking-payment-order-service-meta">
                            {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })} · {selectedTime}
                          </p>
                        </div>
                      </div>
                      <p className="booking-payment-order-price" style={{ textDecoration: 'line-through', opacity: 0.45 }}>
                        {selectedServiceData?.currency}{Number(selectedServiceData?.price).toLocaleString('es-CO')}
                      </p>
                    </div>
                    <div className="booking-payment-order-row" style={{ marginTop: '4px' }}>
                      <p className="booking-payment-order-total-label" style={{ fontSize: '12px', opacity: 0.6 }}>
                        Anticipo de reserva
                      </p>
                      <p className="booking-payment-order-price">
                        {selectedServiceData?.currency}{isMockMode() ? '0' : Number(selectedServiceData?.booking_fee ?? 0).toLocaleString('es-CO')}
                      </p>
                    </div>
                    <div className="booking-payment-order-divider" />
                    <div className="booking-payment-order-row">
                      <p className="booking-payment-order-total-label">Total a pagar ahora</p>
                      <p className="booking-payment-order-total-price">
                        {selectedServiceData?.currency}{isMockMode() ? '0' : Number(selectedServiceData?.booking_fee ?? 0).toLocaleString('es-CO')}
                      </p>
                    </div>
                    {!isMockMode() && (selectedServiceData?.booking_fee ?? 0) < (selectedServiceData?.price ?? 0) && (
                      <p style={{ fontSize: '11px', opacity: 0.5, marginTop: '6px', textAlign: 'right' }}>
                        Resto ({selectedServiceData?.currency}{Number((selectedServiceData?.price ?? 0) - (selectedServiceData?.booking_fee ?? 0)).toLocaleString('es-CO')}) se paga en el local
                      </p>
                    )}
                  </div>
                </div>
                <div className="booking-payment-method-card">
                  <div className="booking-payment-method-header">
                    <div className="booking-payment-method-icon">
                      <i className={isMockMode() ? 'ri-test-tube-line' : 'ri-bank-card-line'} />
                    </div>
                    <div>
                      <p className="booking-payment-method-title">
                        {isMockMode() ? 'Modo de prueba (Mock)' : 'Pago seguro con Wompi'}
                      </p>
                      <p className="booking-payment-method-subtitle">
                        {isMockMode()
                          ? 'Simulación local · Sin cobro real'
                          : 'Tarjeta débito/crédito · PSE · Nequi · Bancolombia'}
                      </p>
                    </div>
                    <span className="booking-payment-wompi-badge">
                      <i className={isMockMode() ? 'ri-flask-line' : 'ri-shield-check-line'} />
                      {isMockMode() ? 'Mock' : 'Seguro'}
                    </span>
                  </div>
                  <div className="booking-payment-wompi-info">
                    <p className="booking-payment-wompi-info-text">
                      {isMockMode()
                        ? 'El pago será simulado localmente. No se realizará ningún cobro real. La cita quedará confirmada automáticamente tras la simulación.'
                        : 'Serás redirigido al portal de pago de Wompi para completar tu transacción de forma segura. Una vez confirmado el pago, tu cita quedará reservada automáticamente.'}
                    </p>
                    {!isMockMode() && (
                      <div className="booking-payment-wompi-methods">
                        <span className="booking-payment-wompi-method-tag"><i className="ri-bank-card-2-line" /> Tarjeta</span>
                        <span className="booking-payment-wompi-method-tag"><i className="ri-building-line" /> PSE</span>
                        <span className="booking-payment-wompi-method-tag"><i className="ri-smartphone-line" /> Nequi</span>
                        <span className="booking-payment-wompi-method-tag"><i className="ri-bank-line" /> Bancolombia</span>
                      </div>
                    )}
                  </div>
                </div>

                {wompiRedirecting && (
                  <div className="booking-wompi-redirecting">
                    <i className="ri-loader-4-line animate-spin" />
                    <p>Redirigiendo a Wompi...</p>
                  </div>
                )}

                {paymentError && (
                  <div className="booking-error-banner mb-6">
                    <i className="ri-error-warning-line" />
                    <p className="booking-error-banner-text">{paymentError}</p>
                  </div>
                )}

                <div className="flex justify-between">
                  <button type="button" onClick={() => setStep(3)} disabled={processingPayment} className="booking-btn-secondary">
                    <i className="ri-arrow-left-line" /> Atrás
                  </button>
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={processingPayment || wompiRedirecting}
                    className="booking-btn-primary"
                  >
                    {processingPayment
                      ? (<><i className="ri-loader-4-line animate-spin" /> {wompiRedirecting ? 'Redirigiendo...' : 'Procesando pago...'}</>)
                      : isMockMode()
                        ? (<><i className="ri-test-tube-line" /> Simular pago</>)
                        : (<><i className="ri-secure-payment-line" /> Pagar con Wompi</>)
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Success screen ── */
          <div className="booking-success">
            <div className="booking-success-icon">
              <i className="ri-checkbox-circle-fill" />
            </div>
            <p className="booking-success-label">◆ Reserva recibida ◆</p>
            <h2 className="booking-success-title">¡Listo!</h2>
            <p className="booking-success-message">
              {clientName ? `¡Gracias, ${clientName.split(' ')[0]}! ` : ''}
              Tu cita ha sido confirmada. Te esperamos el{' '}
              <span className="highlight-white">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>{' '}
              a las <span className="highlight-accent">{selectedTime}</span>.
            </p>
            <div className="booking-success-reminder">
              <div className="booking-success-reminder-icon">
                <i className="ri-alarm-warning-line" />
              </div>
              <p className="booking-success-reminder-text">
                Recuerda llegar <strong>10 minutos antes</strong> de tu cita para una mejor atención.
              </p>
            </div>
            <div className="booking-success-actions">
              <a
                href={buildGoogleCalendarUrl({ serviceName: selectedServiceData?.name ?? 'Cita', date: selectedDate, time: selectedTime })}
                target="_blank"
                rel="noopener noreferrer"
                className="booking-success-cal-btn"
              >
                <div className="booking-success-cal-btn-icon">
                  <i className="ri-calendar-event-line" />
                </div>
                Agregar a Google Calendar
              </a>
              <Link to="/" className="booking-success-home-btn">
                Volver al inicio
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}